import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@coin/shared/core/util-environments';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  MassExportTransactionStatus,
  PaginatedResult,
  TransactionStatus,
  TransactionStatusMetadata
} from '@coin/shared/core/util-models';
import { LetterType } from '@coin/shared/core/util-enums';
import { LoadingService } from '@coin/shared/core/data-access';
import { IncentiveRecordUpdate, RecordAchievementDto, SeasonRecord } from '@coin/shared/incentive/util';
import { PayoutExportService } from '../../shared/interfaces/mass-export-service.interface';
import { RecordsService } from '../interfaces/record-service.interface';
import { PayoutExportRequest } from '../models/mass-export-request.model';
import { IncentiveRecordsService } from './incentive-records.service';

@Injectable({
  providedIn: 'root'
})
export class IncentiveAchievementsService implements RecordsService, PayoutExportService {
  private recordService = inject(IncentiveRecordsService);
  private http = inject(HttpClient);
  private store = inject(Store);
  private toast = inject(ToastrService);
  private loading = inject(LoadingService);
  private incentiveRecordsService = inject(IncentiveRecordsService);

  seasonId: string;
  queryPagedAndFilteredData = this.getRecordsPaginated;

  public getRecordsPaginated(
    page: number,
    size: number,
    searchText?: string,
    filterText?: string,
    sortText?: string
  ): Observable<PaginatedResult<SeasonRecord>> {
    return this.http
      .get<
        PaginatedResult<SeasonRecord>
      >(`${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/achievement?Paging.Page=${page + 1}&Paging.Size=${size}${filterText}${sortText}`)
      .pipe(
        map(data => ({
          ...data,
          content: data.content.map(item => this.recordService.setLetterState(item, LetterType.TargetAchievementForm))
        })),
        catchError(() => of(null)) // do not cancel subscription in simple table component
      );
  }

  public getAvailableRecords(page: number, size: number, filter: string): Observable<PaginatedResult<string>> {
    return this.http
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/achievement?Paging.Page=${page + 1}&Paging.Size=${size}&Query.isDesignReady=false&Query.State=Active&Query.State=Outdated${filter}`)
      .pipe(catchError(() => of(null)));
  }

  public getFieldValues(
    page: number,
    size: number,
    targetProperty: string,
    seasonId: string,
    searchText?: string
  ): Observable<PaginatedResult<string>> {
    const searchQuery = searchText.trim() ? `&${targetProperty}=${searchText}` : '';
    return this.http
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}${`/admin/api/v3/master/seasons/incentive/${seasonId}/records/achievements/field-values`}?Page=${page + 1}&Size=${size}&targetProperty=${targetProperty}${searchQuery}`)
      .pipe(catchError(() => of(null)));
  }

  public getRecordAchievements(recordId: string): Observable<RecordAchievementDto> {
    return this.http.get<RecordAchievementDto>(
      `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/${recordId}/achievements`
    );
  }

  public calculateSingleRecord(recordId: string): Observable<SeasonRecord> {
    return this.http.post<SeasonRecord>(
      `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/${recordId}/calculate`,
      null
    );
  }

  public calculateSelectedRecords(recordIds: string[]): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.http.post<TransactionStatus<TransactionStatusMetadata>>(
      `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/calculate`,
      {
        recordIds
      }
    );
  }

  public calculateFilteredRecords(filterText: string): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.http.post<TransactionStatus<TransactionStatusMetadata>>(
      `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/calculate?${filterText}`,
      {}
    );
  }

  public updateRecord(body: Partial<IncentiveRecordUpdate>, recordId: string): Observable<void> {
    return this.incentiveRecordsService.updateRecord(body, recordId);
  }

  public payoutExport(
    request: PayoutExportRequest,
    filterText?: string
  ): Observable<TransactionStatus<MassExportTransactionStatus>> {
    const filterParams = filterText ? `?${filterText}` : '';

    return this.http.post<TransactionStatus<MassExportTransactionStatus>>(
      `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records/achievements/export${filterParams}`,
      request
    );
  }
}
